import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import { isSsr, AD_PAGES_PREFIX, ADS_STORAGE_KEY } from 'define';
import { getCookie } from 'utils/cookie';
import { useTranslation } from 'staticQueries/translation';
import { EMAIL_DE, EMAIL_EN, EMAIL_LP_EN, germanRegion, americasRegion } from '../define/locale';

const COOKIE_NAME = 'x-country-code';

export function getCountryCode() {
  if (!isSsr) {
    return getCookie(COOKIE_NAME);
  }

  return '';
}

export function isRequestedFrom(countryCode = '') {
  if (Array.isArray(countryCode)) {
    return countryCode.includes(getCountryCode()?.toLowerCase());
  }

  return getCountryCode()?.toLowerCase() === countryCode?.toLowerCase();
}

export const LanguageContext = createContext();

export function useTrans(namespaces) {
  const lang = useLang();
  const isEnClones = lang === 'uk' || lang === 'kz' || lang === 'pl';
  const preparedLang = isEnClones ? 'en' : lang;
  const translationJson = useTranslation(preparedLang);
  const translations = Object.assign(
    {},
    ...namespaces.map(namespace => translationJson[namespace]),
  );
  return key => translations[key];
}

export function useLang() {
  return useContext(LanguageContext);
}

export function withTrans(Component, namespaces) {
  return function WithTrans(props) {
    const t = useTrans(namespaces);
    return <Component {...props} t={t} />;
  };
}

export const withLanguageContext = (Page, lang) => {
  return function WithLanguageContext(props) {
    return (
      <LanguageContext.Provider value={lang}>
        <Page {...props} />
      </LanguageContext.Provider>
    );
  };
};

export const useIsRegion = countryList => {
  const [isRegion, setIsRegion] = useState(false);

  useEffect(() => {
    if (isRequestedFrom(countryList)) {
      setIsRegion(true);
    } else {
      setIsRegion(false);
    }
  }, []);

  return isRegion;
};

export const getDirection = () => (process.env.GATSBY_LANG === 'ae' ? 'rtl' : 'ltr');

const useShowAdEmail = () => {
  const [showAdEmail, setShowAdEmail] = useState(false);
  const currentLink = useLocation().pathname;
  const isAdPage = currentLink === AD_PAGES_PREFIX || currentLink.startsWith(`${AD_PAGES_PREFIX}/`);

  useEffect(() => {
    const ads = window.localStorage.getItem(ADS_STORAGE_KEY);
    if (isAdPage || Boolean(ads)) {
      setShowAdEmail(true);
    }
  }, [isAdPage]);
  return showAdEmail;
};

export const useLocalisedEmail = () => {
  const lang = useLang();
  const showAdEmail = useShowAdEmail();
  const isDachRegion = useIsRegion([...germanRegion, 'nl']);

  if (lang === 'de' || lang === 'nl' || (lang === 'en' && isDachRegion)) {
    return EMAIL_DE;
  }
  if (showAdEmail) {
    return EMAIL_LP_EN;
  }
  return EMAIL_EN;
};

export const useLocalisedPrivacyLinks = () => {
  const lang = useLang();

  const defaultPolicyLink =
    lang === 'ae' || lang === 'az' || lang === 'fr' || lang === 'nl'
      ? 'https://andersenlab.com/privacy-policy'
      : '/privacy-policy';

  const [privacyPolicyLink, setPrivacyPolicyLink] = useState(defaultPolicyLink);

  useEffect(() => {
    if (lang === 'en' && isRequestedFrom('PL')) {
      setPrivacyPolicyLink('/privacy-policy/pl');
      return;
    }

    if (lang === 'en' && isRequestedFrom(americasRegion)) {
      setPrivacyPolicyLink('/privacy-policy/us');
      return;
    }

    if (lang === 'en' && isRequestedFrom('BY')) {
      setPrivacyPolicyLink('/personal_data_processing_policy.pdf');
      return;
    }

    setPrivacyPolicyLink(defaultPolicyLink);
  }, [lang]);

  const cookiesPolicyLink =
    lang === 'ae' || lang === 'az' || lang === 'fr' || lang === 'nl'
      ? 'https://andersenlab.com/cookies-policy'
      : '/cookies-policy';

  return { privacyPolicyLink, cookiesPolicyLink };
};
